import React from 'react';
import { Trash2, Paperclip } from 'lucide-react';
import { Plus, Eye, Download, Check, MessageSquare, Send, X } from './icons';
import { useAuth } from '../contexts/AuthContext';
import { useNotificationContext } from '../contexts/NotificationContext';
import api from '../services/api';
import { downloadEvidenceViaAPI } from '../utils/evidenceProxy';
import EvidenceModal from './EvidenceModal';
import EvidenceItem from './EvidenceItem';
import ExcelTable from './ExcelTable';

const ALLOWED_COMMENT_FILE_TYPES = [
  'image/jpeg', 'image/png', 'image/gif', 'image/webp',
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
];
const MAX_COMMENT_FILES = 5;
const MAX_COMMENT_FILE_SIZE = 10 * 1024 * 1024; // 10MB

// Nombre real del autor de un comentario. `username` suele estar vacío (la
// mayoria de las cuentas se identifican por email, no por username) -- se
// prioriza nombre+apellido, y si quien comento es el solicitante original
// pero no tiene cuenta en HelpDesk (creadoPor null), se cae al nombre real
// del ticket (nombreSolicitante) en vez de un "Usuario" generico.
export function nombreDeAutor(comentario, ticket, esTicketCreator) {
  const autor = comentario.creadoPor;
  if (autor?.nombre) return `${autor.nombre} ${autor.apellido || ''}`.trim();
  if (autor?.username) return autor.username;
  if (esTicketCreator) return ticket?.nombreSolicitante || 'Usuario';
  return 'Usuario';
}

export default function TicketTimeline({ ticket, onAddActualizacion, onActualizacionEliminada, onActualizacionAgregada }) {
  const { user } = useAuth();
  const { alertSuccess, alertError } = useNotificationContext();
  const [comentarios, setComentarios] = React.useState([]);
  const [selectedEvidence, setSelectedEvidence] = React.useState(null);
  const [showEvidenceModal, setShowEvidenceModal] = React.useState(false);

  // Estados del formulario de comentario inline
  const [newCommentText, setNewCommentText] = React.useState('');
  const [selectedFiles, setSelectedFiles] = React.useState([]);
  const [isInternalComment, setIsInternalComment] = React.useState(false);
  // Tabla estilo Excel adjunta al comentario (solo staff/admin) y tablas ya guardadas por comentario
  const TABLA_VACIA = { headers: ['', '', '', '', ''], rows: Array.from({ length: 5 }, () => ['', '', '', '', '']) };
  const [usarTabla, setUsarTabla] = React.useState(false);
  const [tabla, setTabla] = React.useState(TABLA_VACIA);
  const [tablasPorComentario, setTablasPorComentario] = React.useState({});
  const [isSubmittingComment, setIsSubmittingComment] = React.useState(false);
  const [isDraggingComment, setIsDraggingComment] = React.useState(false);

  const addCommentFiles = React.useCallback((incomingFiles) => {
    const files = Array.from(incomingFiles || []);
    if (files.length === 0) return;

    setSelectedFiles(prev => {
      const validos = [];
      for (const file of files) {
        if (!ALLOWED_COMMENT_FILE_TYPES.includes(file.type)) {
          alertError(`Tipo de archivo no permitido: ${file.name || 'archivo'}`);
          continue;
        }
        if (file.size > MAX_COMMENT_FILE_SIZE) {
          alertError(`Archivo demasiado grande: ${file.name}. Máximo 10MB.`);
          continue;
        }
        validos.push(file);
      }
      if (validos.length === 0) return prev;

      const total = prev.length + validos.length;
      if (total > MAX_COMMENT_FILES) {
        alertError(`Máximo ${MAX_COMMENT_FILES} archivos por comentario.`);
        const espacioDisponible = Math.max(0, MAX_COMMENT_FILES - prev.length);
        return [...prev, ...validos.slice(0, espacioDisponible)];
      }
      return [...prev, ...validos];
    });
  }, [alertError]);

  const handleCommentDragOver = (e) => {
    e.preventDefault();
    setIsDraggingComment(true);
  };

  const handleCommentDragLeave = (e) => {
    e.preventDefault();
    setIsDraggingComment(false);
  };

  const handleCommentDrop = (e) => {
    e.preventDefault();
    setIsDraggingComment(false);
    addCommentFiles(e.dataTransfer.files);
  };

  const handleCommentPaste = (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;

    const pastedImages = [];
    for (const item of items) {
      if (item.kind === 'file' && item.type.startsWith('image/')) {
        const file = item.getAsFile();
        if (file) {
          // Las imágenes pegadas desde el portapapeles no traen nombre -- se les asigna uno
          const renamed = new File([file], `imagen-pegada-${Date.now()}.png`, { type: file.type });
          pastedImages.push(renamed);
        }
      }
    }
    if (pastedImages.length > 0) {
      addCommentFiles(pastedImages);
    }
  };

  // Estados del ticket con colores
  const estados = React.useMemo(() => [
    { nombre: 'Pendiente', color: 'bg-yellow-500', descripcion: 'Ticket creado' },
    { nombre: 'Asignado', color: 'bg-blue-500', descripcion: 'Ticket asignado' },
    { nombre: 'En Proceso', color: 'bg-orange-500', descripcion: 'En resolución' },
    { nombre: 'Resuelto', color: 'bg-green-500', descripcion: 'Resuelto por soporte' },
    { nombre: 'Esperando Usuario', color: 'bg-indigo-500', descripcion: 'Esperando confirmación' },
    { nombre: 'Cerrado', color: 'bg-gray-500', descripcion: 'Cerrado' }
  ], []);

  // Función para obtener el estado actual del ticket
  const getEstadoActual = React.useCallback(() => {
    const index = estados.findIndex(e => e.nombre === ticket.estado);
    return index >= 0 ? index : 0;
  }, [ticket.estado, estados]);

  // Cargar comentarios del ticket
  const cargarComentarios = React.useCallback(async () => {
    if (!ticket?.id) return;

    try {
      const response = await api.get(`/tickets/${ticket.id}/comentarios`);
      let allComments = response.data;

      api.get(`/tickets/${ticket.id}/tablas`)
        .then(({ data }) => {
          const mapa = {};
          (Array.isArray(data) ? data : []).forEach((t) => { mapa[t.comentarioId] = t; });
          setTablasPorComentario(mapa);
        })
        .catch(() => setTablasPorComentario({}));

      // Detectar si existe un comentario de sistema con la evidencia inicial
      const evidenceCommentIndex = allComments.findIndex(c =>
        (c.creadoPor?.username === 'Sistema' || !c.creadoPor) &&
        c.contenido === 'Evidencia adjunta al crear el ticket'
      );

      let initialEvidence = null;
      let initialTicketFiles = ticket.archivos || [];

      // Si existe el comentario de sistema, extrar evidencia y removerlo
      if (evidenceCommentIndex !== -1) {
        const evidenceComment = allComments[evidenceCommentIndex];
        initialEvidence = evidenceComment.evidencia;

        if (evidenceComment.archivosTicket && evidenceComment.archivosTicket.length > 0) {
          const existingIds = new Set(initialTicketFiles.map(f => f.id));
          const newFiles = evidenceComment.archivosTicket.filter(f => !existingIds.has(f.id));
          initialTicketFiles = [...initialTicketFiles, ...newFiles];
        }

        allComments.splice(evidenceCommentIndex, 1);
      }

      // Inyectar descripción como primer comentario
      if (ticket.descripcion) {
        const descripcionComment = {
          id: `description-${ticket.id}`,
          contenido: ticket.descripcion,
          fechaCreacion: ticket.fechaCreacion,
          creadoPor: ticket.creadoPor || { username: ticket.nombreSolicitante || 'Solicitante' },
          esInterno: false,
          archivosTicket: initialTicketFiles,
          evidencia: initialEvidence,
          esDescripcion: true
        };
        allComments = [descripcionComment, ...allComments];
      }

      // Ordenar cronológicamente ascendente
      allComments.sort((a, b) => {
        const dateA = new Date(a.fechaCreacion);
        const dateB = new Date(b.fechaCreacion);
        if (dateA.getTime() === dateB.getTime()) {
          if (a.id && a.id.toString().startsWith('description')) return -1;
          if (b.id && b.id.toString().startsWith('description')) return 1;
          return (Number(a.id) || 0) - (Number(b.id) || 0);
        }
        return dateA - dateB;
      });

      setComentarios(allComments);
    } catch (error) {
      console.error('Error al cargar comentarios:', error);
    }
  }, [ticket]);

  React.useEffect(() => {
    cargarComentarios();
  }, [cargarComentarios]);

  const handleFileSelect = (e) => {
    addCommentFiles(e.target.files);
    e.target.value = '';
  };

  const handleInlineSubmitComment = async (e) => {
    e.preventDefault();
    if (!newCommentText.trim()) return;

    setIsSubmittingComment(true);
    try {
      let evidenciaUrl = null;

      if (selectedFiles.length > 0) {
        const primerArchivo = selectedFiles[0];
        const formDataFile = new FormData();
        formDataFile.append('file', primerArchivo);

        const uploadResponse = await api.post('/tickets/upload-evidence', formDataFile, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        evidenciaUrl = uploadResponse.data.url;
        if (evidenciaUrl && !evidenciaUrl.startsWith('/')) {
          evidenciaUrl = `/${evidenciaUrl}`;
        }
      }

      const payload = {
        contenido: newCommentText.trim(),
        esInterno: isInternalComment,
        evidencia: evidenciaUrl,
      };

      const respuestaComentario = await api.post(`/tickets/${ticket.id}/comentarios`, payload);

      const tablaTieneDatos = usarTabla && (tabla.headers.some((h) => h.trim()) || tabla.rows.some((f) => f.some((c) => c.trim())));
      if (tablaTieneDatos && respuestaComentario.data?.id) {
        try {
          await api.post(`/tickets/${ticket.id}/comentarios/${respuestaComentario.data.id}/tabla`, tabla);
        } catch (err) {
          console.error('Error al guardar la tabla del comentario:', err);
          alertError('El comentario se envió, pero no se pudo guardar la tabla.');
        }
      }

      if (selectedFiles.length > 1) {
        const adicionales = selectedFiles.slice(1);
        const formDataArchivos = new FormData();
        adicionales.forEach(f => formDataArchivos.append('archivos', f));

        try {
          await api.post(`/tickets/${ticket.id}/archivos`, formDataArchivos, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
        } catch (err) {
          console.error('Error al subir archivos adicionales:', err);
        }
      }

      alertSuccess('Comentario agregado exitosamente');
      setNewCommentText('');
      setSelectedFiles([]);
      setIsInternalComment(false);
      setUsarTabla(false);
      setTabla(TABLA_VACIA);

      await cargarComentarios();

      if (onActualizacionAgregada) {
        onActualizacionAgregada();
      }
    } catch (error) {
      console.error('Error al enviar comentario:', error);
      alertError(error.response?.data?.message || 'Error al enviar el comentario');
    } finally {
      setIsSubmittingComment(false);
    }
  };

  // Función para eliminar actualización
  const eliminarActualizacion = async (comentarioId) => {
    if (!window.confirm('¿Estás seguro de que quieres eliminar esta actualización?')) {
      return;
    }

    try {
      await api.delete(`/tickets/${ticket.id}/comentarios/${comentarioId}`);
      if (onActualizacionEliminada) {
        onActualizacionEliminada();
      }
      // Recargar comentarios localmente
      const response = await api.get(`/tickets/${ticket.id}/comentarios`);
      setComentarios(response.data.sort((a, b) => new Date(a.fechaCreacion) - new Date(b.fechaCreacion)));
    } catch (error) {
      console.error('Error al eliminar actualización:', error);
      alert('Error al eliminar la actualización');
    }
  };

  // Función para abrir modal de evidencia o descargar si es documento
  const handleOpenEvidence = (evidenceUrl, evidenceName, mimeType) => {
    // Si tenemos el tipo mime y NO es imagen, descargar
    const isImage = mimeType ? mimeType.startsWith('image/') : true; // Si no hay mime, asumimos imagen por defecto (comportamiento legacy) o intentamos detectar por extensión

    // Detección por extensión si mimeType no está definido
    let shouldDownload = !isImage;
    if (!mimeType && evidenceUrl) {
      const extension = evidenceUrl.split('.').pop().toLowerCase();
      if (['pdf', 'doc', 'docx', 'xls', 'xlsx', 'txt', 'csv', 'zip', 'rar'].includes(extension)) {
        shouldDownload = true;
      }
    }

    if (shouldDownload) {
      downloadEvidenceViaAPI(evidenceUrl, evidenceName);
    } else {
      setSelectedEvidence({ url: evidenceUrl, name: evidenceName });
      setShowEvidenceModal(true);
    }
  };

  const handleCloseEvidence = () => {
    setShowEvidenceModal(false);
    setSelectedEvidence(null);
  };

  if (!ticket) return null;

  const estadoActualIndex = getEstadoActual();

  return (
    <div className="space-y-8">
      {/* 1. Horizontal Stepper */}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6 overflow-x-auto">
        <h2 className="text-lg font-semibold mb-6 text-gray-900 dark:text-white">Estado del Ticket</h2>
        <div className="flex items-center justify-between min-w-[600px]">
          {estados.map((estado, index) => {
            const isCompleted = index <= estadoActualIndex;
            const isCurrent = index === estadoActualIndex;

            return (
              <div key={estado.nombre} className="flex-1 relative flex flex-col items-center group">
                {/* Connecting Line */}
                {index < estados.length - 1 && (
                  <div className={`absolute top-4 left-1/2 w-full h-1 ${index < estadoActualIndex ? 'bg-blue-500' : 'bg-gray-200 dark:bg-gray-700'
                    }`} />
                )}

                {/* Circle */}
                <div className={`relative z-10 flex items-center justify-center w-8 h-8 rounded-full border-2 transition-colors duration-200 ${isCompleted
                  ? `${estado.color} border-${estado.color.replace('bg-', '')} text-white`
                  : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-300'
                  } ${isCurrent ? 'ring-2 ring-offset-2 ring-blue-500 dark:ring-offset-gray-800' : ''}`}>
                  {isCompleted ? <Check className="w-5 h-5" /> : <span className="w-2 h-2 rounded-full bg-gray-300 dark:bg-gray-600" />}
                </div>

                {/* Label */}
                <div className="mt-2 text-center">
                  <span className={`text-xs font-medium block ${isCurrent ? 'text-gray-900 dark:text-white' : 'text-gray-500 dark:text-gray-400'
                    }`}>
                    {estado.nombre}
                  </span>
                  {isCurrent && (
                    <span className="text-[10px] text-gray-400 dark:text-gray-500 block max-w-[80px] truncate">
                      Actual
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* 2. Updates Section */}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Avances y Actualizaciones</h2>

          {(!['Cerrado', 'Cancelado'].includes(ticket.estado)) &&
            (user?.role === 'admin' ||
              user?.role === 'soporte' ||
              user?.role === 'sap') && (
              <button
                onClick={() => {
                  document.getElementById('inline-comment-section')?.scrollIntoView({ behavior: 'smooth' });
                  document.getElementById('inline-comment-textarea')?.focus();
                }}
                className="px-3 py-1.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors flex items-center"
              >
                <Plus className="w-4 h-4 mr-1.5" />
                Agregar Avance
              </button>
            )}
        </div>

        <div className="space-y-6">
          {comentarios.length === 0 ? (
            <p className="text-center text-gray-500 dark:text-gray-400 py-4">No hay actualizaciones registradas.</p>
          ) : (
            <div className="relative border-l-2 border-gray-200 dark:border-gray-700 ml-3 space-y-8">
              {comentarios.map((comentario, index) => {
                if (comentario.esInterno) return null; // Skip internal comments if managed elsewhere or keep if unified

                // Determine layout based on content
                const esDerivacion = (comentario.contenido || '').includes('Derivación') || (comentario.contenido || '').includes('📌') || (comentario.contenido || '').includes('✅') || (comentario.contenido || '').includes('❌') || (comentario.contenido || '').includes('🚫');
                const esAsignacion = (comentario.contenido || '').toLowerCase().includes('asignado') || esDerivacion;
                const isTicketCreator = (ticket.creadoPor?.id && comentario.creadoPor?.id && ticket.creadoPor.id === comentario.creadoPor.id) ||
                  (comentario.creadoPor?.username === ticket.nombreSolicitante) ||
                  (comentario.creadoPor?.email === ticket.emailSolicitante);
                // Detect if it's a system log based on content patterns or author
                const isSystemLog = (comentario.contenido || '').match(/^(Ticket asignado|Estado actualizado|Prioridad actualizada|Ticket cerrado|Ticket reabierto|Evidencia adjunta|📌|✅|❌|🚫|Derivación)/i) ||
                  comentario.creadoPor?.username === 'Sistema' ||
                  !comentario.creadoPor ||
                  esDerivacion;

                return (
                  <div key={comentario.id} className="relative pl-6">
                    {/* Dot */}
                    <div className={`absolute -left-[9px] top-0 w-4 h-4 rounded-full border-2 border-white dark:border-gray-800 ${esDerivacion ? 'bg-purple-500' :
                      esAsignacion ? 'bg-purple-500' :
                      isTicketCreator ? 'bg-orange-500' :
                        isSystemLog ? 'bg-gray-400' : 'bg-blue-600'
                      }`}></div>

                    {/* Content Card */}
                    <div className="flex flex-col sm:flex-row sm:items-baseline sm:justify-between mb-1">
                      <div className="flex items-center space-x-2">
                        <span className={`text-sm font-bold ${esDerivacion ? 'text-purple-600 dark:text-purple-400' :
                          esAsignacion ? 'text-purple-600' :
                          isTicketCreator ? 'text-orange-600 dark:text-orange-400' :
                            isSystemLog ? 'text-gray-600' : 'text-blue-700 dark:text-blue-400'
                          }`}>
                          {esDerivacion
                            ? (comentario.creadoPor?.nombre || comentario.creadoPor?.username || 'Sistema')
                            : isTicketCreator
                              ? nombreDeAutor(comentario, ticket, true)
                              : isSystemLog
                                ? 'Sistema'
                                : nombreDeAutor(comentario, ticket, false)}
                          {isTicketCreator && <span className="text-xs font-normal text-gray-500 ml-1">(Usuario)</span>}
                        </span>


                        {/* Display historical state if available */}
                        {(comentario.estadoCreacion || comentario.estadoTicket || comentario.estado) && (
                          <span className="text-xs font-medium text-gray-500 dark:text-gray-400 px-2 py-0.5 bg-gray-100 dark:bg-gray-700 rounded-full">
                            ({comentario.estadoCreacion || comentario.estadoTicket || comentario.estado})
                          </span>
                        )}

                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          {new Date(comentario.fechaCreacion).toLocaleString('es-ES')}
                        </span>
                      </div>

                      {(user?.id === comentario.creadoPor?.id || user?.role === 'admin') && (
                        <button
                          onClick={() => eliminarActualizacion(comentario.id)}
                          className="text-gray-400 hover:text-red-500 transition-colors ml-auto sm:ml-4"
                          title="Eliminar actualización"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>

                    <div className={`rounded-lg p-3 border ${isTicketCreator
                      ? 'bg-orange-50 border-orange-100 dark:bg-orange-900/10 dark:border-orange-800'
                      : 'bg-blue-50 border-blue-100 dark:bg-blue-900/10 dark:border-blue-800'
                      }`}>
                      <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                        {comentario.contenido ? comentario.contenido.replace(/^[📌✅❌🚫📍]\s*/, '').trim() : ''}
                      </p>

                      {tablasPorComentario[comentario.id] && (
                        <div className="mt-3">
                          <ExcelTable
                            titulo="Tabla adjunta"
                            headers={tablasPorComentario[comentario.id].headers}
                            data={tablasPorComentario[comentario.id].rows}
                            maxHeight="max-h-80"
                          />
                        </div>
                      )}

                      {/* Evidence/Files */}
                      {(comentario.evidencia || (comentario.archivosTicket && comentario.archivosTicket.length > 0)) && (
                        <div className="mt-3 grid grid-cols-2 gap-2">
                          {/* Handle Evidence Logic Here (Simplifying for brevity, reuse logic from before) */}
                          {/* Reusing the rendering logic for files essentially */}
                          {comentario.evidencia && (
                            <div className="col-span-1">
                              <EvidenceItem
                                url={comentario.evidencia}
                                name="Evidencia"
                                onView={() => handleOpenEvidence(comentario.evidencia, 'Evidencia')}
                              />
                            </div>
                          )}
                          {comentario.archivosTicket?.map(f => (
                            <div key={f.id} className="col-span-1">
                              <EvidenceItem
                                url={`/securefile/preview/evidencias/${f.nombreArchivo}`}
                                name={f.nombreOriginal}
                                mime={f.tipoMime}
                                size={f.tamañoBytes}
                                onView={() => handleOpenEvidence(`/securefile/preview/evidencias/${f.nombreArchivo}`, f.nombreOriginal, f.tipoMime)}
                              />
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Formulario de Comentarios Inline */}
          <div id="inline-comment-section" className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
            <h4 className="text-sm font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
              <MessageSquare className="w-4 h-4 text-blue-600 dark:text-blue-400" />
              Agregar Comentario / Avance
            </h4>

            <form
              onSubmit={handleInlineSubmitComment}
              className="space-y-3"
              onDragOver={handleCommentDragOver}
              onDragLeave={handleCommentDragLeave}
              onDrop={handleCommentDrop}
            >
              <div className="relative">
                <textarea
                  id="inline-comment-textarea"
                  rows={3}
                  value={newCommentText}
                  onChange={(e) => setNewCommentText(e.target.value)}
                  onPaste={handleCommentPaste}
                  placeholder="Escribe un comentario o respuesta... (puedes arrastrar o pegar imágenes aquí)"
                  className={`w-full bg-white dark:bg-gray-700 border-2 rounded-xl p-3 text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 outline-none transition-all resize-y ${isDraggingComment ? 'border-blue-500 border-dashed bg-blue-50 dark:bg-blue-900/20' : 'border-gray-300 dark:border-gray-600'
                    }`}
                />
                {isDraggingComment && (
                  <div className="absolute inset-0 flex items-center justify-center bg-blue-50/90 dark:bg-blue-900/60 rounded-xl pointer-events-none">
                    <span className="text-sm font-medium text-blue-700 dark:text-blue-300 flex items-center gap-2">
                      <Paperclip className="w-4 h-4" />
                      Suelta el archivo aquí
                    </span>
                  </div>
                )}
              </div>

              {/* Archivos adjuntos seleccionados */}
              {selectedFiles.length > 0 && (
                <div className="flex flex-wrap gap-2 pt-1">
                  {selectedFiles.map((file, idx) => (
                    <div key={idx} className="flex items-center gap-1.5 bg-blue-50 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 text-xs px-3 py-1.5 rounded-lg border border-blue-200 dark:border-blue-700">
                      <Paperclip className="w-3.5 h-3.5 shrink-0" />
                      <span className="max-w-[150px] truncate">{file.name}</span>
                      <button
                        type="button"
                        onClick={() => setSelectedFiles(selectedFiles.filter((_, i) => i !== idx))}
                        className="text-blue-600 dark:text-blue-400 hover:text-red-600 dark:hover:text-red-400 ml-1"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {['admin', 'soporte'].includes(user?.role?.toLowerCase()) && usarTabla && (
                <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3 bg-white dark:bg-gray-800">
                  <ExcelTable
                    titulo="Tabla adjunta al comentario"
                    headers={tabla.headers}
                    data={tabla.rows}
                    editable
                    maxHeight="max-h-72"
                    onChange={({ headers, rows }) => setTabla({ headers, rows })}
                  />
                </div>
              )}

              <div className="flex flex-wrap items-center justify-between gap-3 pt-1">
                <div className="flex items-center gap-3">
                  <label className="cursor-pointer inline-flex items-center gap-1.5 text-xs font-medium text-gray-600 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 px-3 py-2 rounded-lg transition-colors">
                    <Paperclip className="w-4 h-4" />
                    <span>Adjuntar evidencia</span>
                    <input
                      type="file"
                      multiple
                      onChange={handleFileSelect}
                      className="hidden"
                      accept="image/*,.pdf,.doc,.docx,.xls,.xlsx"
                    />
                  </label>

                  {['admin', 'soporte'].includes(user?.role?.toLowerCase()) && (
                    <button
                      type="button"
                      onClick={() => setUsarTabla((v) => !v)}
                      className={`inline-flex items-center gap-1.5 text-xs font-medium px-3 py-2 rounded-lg transition-colors ${usarTabla
                        ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'}`}
                      title="Adjuntar datos como tabla (se pueden pegar desde Excel)"
                    >
                      <span>{usarTabla ? 'Quitar tabla' : 'Adjuntar tabla'}</span>
                    </button>
                  )}

                  {['admin', 'soporte'].includes(user?.role?.toLowerCase()) && (
                    <label className="inline-flex items-center gap-2 text-xs font-medium text-gray-600 dark:text-gray-300 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={isInternalComment}
                        onChange={(e) => setIsInternalComment(e.target.checked)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 h-4 w-4"
                      />
                      <span>Comentario interno</span>
                    </label>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={isSubmittingComment || !newCommentText.trim()}
                  className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-medium px-5 py-2 rounded-lg text-sm shadow-sm transition-all"
                >
                  {isSubmittingComment ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      <span>Enviando...</span>
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      <span>Enviar Comentario</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      <EvidenceModal
        isOpen={showEvidenceModal}
        onClose={handleCloseEvidence}
        evidenceUrl={selectedEvidence?.url}
        evidenceName={selectedEvidence?.name}
      />
    </div>
  );
}


